import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "../auth/AuthLayout";
import { Button, TextField, Checkbox, Callout } from "@/design/components";
import { TierSelect } from "./TierSelect";
import { useAuth } from "@/app/auth";
import { registerSocial, type TierId } from "@/api/auth";
import { ApiError } from "@/api/client";
import { PROVIDER_LABEL, type SocialProvider, type SocialSignupPrefill } from "@/api/oauth";
import { TERMS_VERSION, LEGAL_TERMS_URL, LEGAL_PRIVACY_URL } from "@/config";
import authStyles from "../auth/AuthForm.module.css";
import styles from "./RegisterScreen.module.css";

/**
 * Social sign-up finish step (Phase 7). The OAuth callback handed us a verified profile
 * (email already verified by the provider) via a signed `#social` token, so there is NO
 * password to choose — we only collect what social can't supply: the org name, the plan,
 * and the (legally required) T&C acceptance. On submit the API creates the org, links the
 * provider, and returns a session → straight into the app.
 */
export function SocialRegisterScreen({ token, prefill }: { token: string; prefill: SocialSignupPrefill }) {
  const navigate = useNavigate();
  const { establishSession } = useAuth();
  const [orgName, setOrgName] = useState("");
  const [adminName, setAdminName] = useState(prefill.name ?? "");
  const [tier, setTier] = useState<TierId>("free");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ orgName?: string; accepted?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const providerLabel = PROVIDER_LABEL[prefill.provider as SocialProvider] ?? "your provider";

  // Scrub the token from the address bar/history (it shouldn't linger after landing).
  useEffect(() => {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, []);

  // A provider that shared no email can't complete sign-up server-side — send them to
  // email sign-up rather than a guaranteed failure.
  if (!prefill.email) {
    return (
      <AuthLayout>
        <Callout tone="warning" title="We need your email">
          {providerLabel} didn't share an email address, so we can't finish here. Please{" "}
          <Link to="/register">sign up with your email</Link> instead.
        </Callout>
      </AuthLayout>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const fe: { orgName?: string; accepted?: string } = {};
    if (!orgName.trim()) fe.orgName = "Organization name is required";
    if (!accepted) fe.accepted = "You must accept the Terms to continue";
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await registerSocial({
        orgName: orgName.trim(),
        adminName: adminName.trim() || undefined,
        tier,
        acceptedTerms: true,
        termsVersion: TERMS_VERSION,
        social: token,
      });
      // Establish the session, then show the one-time Org_ID reveal (same screen the email
      // path uses) BEFORE entering the app. `signedIn` tells Welcome we're already
      // authenticated + email-verified, so its CTA goes to the dashboard, not sign-in.
      await establishSession(res.token);
      navigate("/welcome", {
        replace: true,
        state: { orgId: res.orgId, orgName: orgName.trim(), email: prefill.email, signedIn: true },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't finish sign-up. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <header className={authStyles.head}>
        <p className="u-eyebrow">Almost there</p>
        <h2 className={authStyles.title}>Finish creating your organization</h2>
      </header>

      <form className={authStyles.form} onSubmit={onSubmit} noValidate>
        {error && <div className={authStyles.formError} role="alert">{error}</div>}

        <Callout tone="info" title={`Continuing with ${providerLabel}`}>
          Signed in as <strong>{prefill.email}</strong>. We'll link {providerLabel} to your new account — no password needed.
        </Callout>

        <TextField
          label="Organization name"
          placeholder="Acme Corp"
          required
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          error={fieldErrors.orgName}
        />
        <TextField
          label="Your name"
          placeholder="Alex Rivera"
          value={adminName}
          onChange={(e) => setAdminName(e.target.value)}
        />

        <div className={styles.tierBlock}>
          <p className={styles.tierLabel}>Starting plan</p>
          <TierSelect value={tier} onChange={setTier} />
          <p className={styles.tierHint}>Start free — upgrade anytime from billing. No card needed for Free.</p>
        </div>

        <Checkbox
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          error={fieldErrors.accepted}
          label={
            <>
              I have read and accept the{" "}
              <a href={LEGAL_TERMS_URL} target="_blank" rel="noreferrer">
                Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a href={LEGAL_PRIVACY_URL} target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              .
            </>
          }
        />

        <Button type="submit" size="lg" fullWidth loading={submitting}>
          Create organization
        </Button>
      </form>

      <p className={authStyles.alt}>
        Prefer email and password? <Link to="/register">Sign up that way</Link>
      </p>
    </AuthLayout>
  );
}
